import { PERFIS_ADMINISTRADOR } from '@/types/domain'
import type { UserToken } from '@/types/auth'

export const ACAO = {
  VerDashboard: 'dashboard.ver',
  VerAutorizacoes: 'autorizacoes.ver',
  CancelarAutorizacao: 'autorizacoes.cancelar',
  CancelarAutorizacaoManual: 'autorizacoes.cancelar-manual',
  AtualizarStatusAutorizacao: 'autorizacoes.atualizar-status',
  VerCredenciais: 'credenciais.ver',
  EditarCredencial: 'credenciais.editar',
  AlterarSituacaoCredencial: 'credenciais.alterar-situacao',
  VerTiposCredenciais: 'tipos-credenciais.ver',
  EditarTipoCredencial: 'tipos-credenciais.editar',
  VerClientes: 'clientes.ver',
  EditarCliente: 'clientes.editar',
  VerRelatorios: 'relatorios.ver',
  GerenciarOperadores: 'operadores.gerenciar',
  GerenciarAdministradores: 'administradores.gerenciar',
  GerenciarPermissoes: 'permissoes.gerenciar',
} as const

export type Acao = (typeof ACAO)[keyof typeof ACAO]

const isAdmin = (user: UserToken | null): boolean => PERFIS_ADMINISTRADOR.includes(user?.perfil ?? '')

const acoesConcedidas = (user: UserToken | null): Acao[] => {
  if (!user) return []

  const claim = user.claims?.find((item) => item.type === 'permissoes')
  if (!claim?.value) return []

  return claim.value.split(',').map((acao) => acao.trim()) as Acao[]
}

export const can = (user: UserToken | null, acao: Acao): boolean => {
  if (!user) return false
  if (isAdmin(user)) return true

  return acoesConcedidas(user).includes(acao)
}

export const isAdminUser = isAdmin

export const ACOES_EXCLUSIVAS_DE_ADMINISTRADOR: Acao[] = [
  ACAO.GerenciarOperadores,
  ACAO.GerenciarAdministradores,
  ACAO.GerenciarPermissoes,
]

export const podeSerConcedidaAOperador = (acao: Acao): boolean =>
  !ACOES_EXCLUSIVAS_DE_ADMINISTRADOR.includes(acao)

export const ACAO_LABEL: Record<Acao, string> = {
  [ACAO.VerDashboard]: 'Ver dashboard',
  [ACAO.VerAutorizacoes]: 'Ver autorizações',
  [ACAO.CancelarAutorizacao]: 'Cancelar autorização',
  [ACAO.CancelarAutorizacaoManual]: 'Cancelar autorização manualmente',
  [ACAO.AtualizarStatusAutorizacao]: 'Atualizar status da autorização',
  [ACAO.VerCredenciais]: 'Ver credenciais',
  [ACAO.EditarCredencial]: 'Editar credencial',
  [ACAO.AlterarSituacaoCredencial]: 'Ativar/inativar credencial',
  [ACAO.VerTiposCredenciais]: 'Ver tipos de credencial',
  [ACAO.EditarTipoCredencial]: 'Criar/editar tipo de credencial',
  [ACAO.VerClientes]: 'Ver clientes',
  [ACAO.EditarCliente]: 'Editar cliente',
  [ACAO.VerRelatorios]: 'Ver relatórios',
  [ACAO.GerenciarOperadores]: 'Gerenciar operadores',
  [ACAO.GerenciarAdministradores]: 'Gerenciar administradores',
  [ACAO.GerenciarPermissoes]: 'Gerenciar permissões',
}

export interface GrupoAcoes {
  id: string
  titulo: string
  acoes: Acao[]
}

const GRUPO_TITULO: Record<string, string> = {
  'tipos-credenciais': 'Tipos de credencial',
}

export const agruparAcoesPorCategoria = (acoes: Acao[]): GrupoAcoes[] => {
  const grupos = new Map<string, GrupoAcoes>()

  for (const acao of acoes) {
    const [prefixo] = acao.split('.')
    const titulo = GRUPO_TITULO[prefixo] ?? prefixo.charAt(0).toUpperCase() + prefixo.slice(1)

    const grupo = grupos.get(prefixo) ?? { id: prefixo, titulo, acoes: [] }
    grupo.acoes.push(acao)
    grupos.set(prefixo, grupo)
  }

  return Array.from(grupos.values())
}

